// Scales & note mapping — pure logic, unit-tested (docs/05 §5.7).
// Melodic instruments are scale-locked: they play scale *degrees*, which this
// module resolves to concrete note names so a wrong note is impossible.

import type { ScaleName } from "./types.ts";

// Semitone offsets from the root for each scale.
const SCALE_INTERVALS: Record<ScaleName, readonly number[]> = {
  major: [0, 2, 4, 5, 7, 9, 11],
  minor: [0, 2, 3, 5, 7, 8, 10],
  majorPentatonic: [0, 2, 4, 7, 9],
  minorPentatonic: [0, 3, 5, 7, 10],
  dorian: [0, 2, 3, 5, 7, 9, 10],
  blues: [0, 3, 5, 6, 7, 10],
  chromatic: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
};

const NOTE_NAMES = [
  "C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B",
] as const;

const NOTE_TO_SEMITONE: Record<string, number> = {
  C: 0, "C#": 1, Db: 1, D: 2, "D#": 3, Eb: 3, E: 4, F: 5, "F#": 6,
  Gb: 6, G: 7, "G#": 8, Ab: 8, A: 9, "A#": 10, Bb: 10, B: 11,
};

/** Keys offered in the UI. */
export const KEY_NAMES: readonly string[] = NOTE_NAMES;

/** Scales offered in the UI, with friendly labels. */
export const SCALE_OPTIONS: ReadonlyArray<{ value: ScaleName; label: string }> = [
  { value: "majorPentatonic", label: "Major pentatonic" },
  { value: "minorPentatonic", label: "Minor pentatonic" },
  { value: "major", label: "Major" },
  { value: "minor", label: "Minor" },
  { value: "dorian", label: "Dorian" },
  { value: "blues", label: "Blues" },
  { value: "chromatic", label: "Chromatic" },
];

/**
 * Resolve a scale degree to a note name (e.g. "E4").
 * Degrees past the top of the scale roll into higher octaves; negative degrees
 * roll lower. `octave` is the base octave for degree 0.
 */
export function degreeToNote(
  degree: number,
  key: string,
  scale: ScaleName,
  octave: number,
): string {
  const intervals = SCALE_INTERVALS[scale];
  const n = intervals.length;
  const octaveShift = Math.floor(degree / n);
  const idx = ((degree % n) + n) % n;
  const root = NOTE_TO_SEMITONE[key] ?? 0;
  const semitoneSum = root + (intervals[idx] ?? 0);
  const noteIndex = ((semitoneSum % 12) + 12) % 12;
  const finalOctave = octave + octaveShift + Math.floor(semitoneSum / 12);
  return `${NOTE_NAMES[noteIndex]}${finalOctave}`;
}

/** Strip the octave from a note name ("C#4" → "C#"). */
export function pitchClass(note: string): string {
  return note.replace(/-?\d+$/, "");
}
